import { Button, Text, useToastContext } from "@ttc3k/trekker";
import { WarningTriangle } from "iconoir-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Flex } from "styled-system/jsx";
import {
  PeopleManySimpleDocument,
  PersonDetailsQuery,
  usePersonDeleteCompletelyByInternalMutation,
} from "gql/generated";
import { ConfirmInputModal, ViewSubSection } from "shared/components";
import { usePostHog } from "shared/hooks";
import { getMultilingualString } from "shared/utils";

type AdminSectionProps = {
  person: PersonDetailsQuery["personDetails"];
  onDeleted: () => void;
};

type AdminOrganization = NonNullable<
  NonNullable<PersonDetailsQuery["personDetails"]>["adminOrganizations"]
>[number];

const confirmPhrase = "DELETE PERSON";

export const AdminSection = ({ person, onDeleted }: AdminSectionProps) => {
  const { t } = useTranslation("adminland", {
    keyPrefix: "PAGE.PEOPLE.ADMIN_SECTION",
  });
  const { t: tCore } = useTranslation("core");
  const { toastFactory } = useToastContext();
  const { trackPostHogException } = usePostHog();
  const [isConfirmingDelete, setIsConfirmingDelete] = useState(false);
  const organizations = (person?.adminOrganizations ?? []).filter(
    (organization): organization is NonNullable<AdminOrganization> =>
      !!organization,
  );

  const [deletePerson, { loading: isDeleting }] =
    usePersonDeleteCompletelyByInternalMutation({
      refetchQueries: [PeopleManySimpleDocument],
    });

  const handleDelete = async () => {
    if (!person?._id) return;

    try {
      await deletePerson({
        variables: {
          personId: person._id,
        },
      });
      toastFactory.create({
        title: tCore("SUCCESS"),
        description: t("MESSAGES.SUCCESS"),
      });
      setIsConfirmingDelete(false);
      onDeleted();
    } catch (error) {
      trackPostHogException(error, {
        fileName: "AdminSection.tsx",
        method: "handleDelete",
        message: "Error deleting person completely",
      });
      toastFactory.create({
        title: tCore("ERROR.TITLE"),
        description:
          error instanceof Error ? error.message : t("MESSAGES.ERROR"),
      });
    }
  };

  return (
    <>
      <ViewSubSection title={t("TITLE")} description={t("DESCRIPTION")}>
        <Flex direction="column" gap="300" alignItems="flex-start">
          <Flex direction="column" gap="150" w="full">
            <Text visual="smallSemiBold">{t("ORG_APPS_TITLE")}</Text>
            {organizations.length > 0 ? (
              <Flex direction="column" gap="100" w="full">
                {organizations.map(organization => (
                  <Flex
                    key={organization._id}
                    py="150"
                    px="200"
                    border="1px solid"
                    borderColor="border.light"
                    borderRadius="md"
                  >
                    <Text visual="smallRegular">
                      {getMultilingualString(organization.name ?? {})}
                    </Text>
                  </Flex>
                ))}
              </Flex>
            ) : (
              <Text visual="smallRegular" color="text.mid">
                {t("NO_ORG_APPS")}
              </Text>
            )}
          </Flex>

          <Button
            visual="destructive"
            isLoading={isDeleting}
            onClick={() => setIsConfirmingDelete(true)}
            w="fit-content"
          >
            {t("DELETE_BUTTON")}...
          </Button>
        </Flex>
      </ViewSubSection>
      <ConfirmInputModal
        isOpen={isConfirmingDelete}
        onClose={() => setIsConfirmingDelete(false)}
        title={t("CONFIRM_MODAL.TITLE")}
        HeaderIcon={WarningTriangle}
        cancelButtonProps={{
          onClick: () => setIsConfirmingDelete(false),
        }}
        saveButtonCopy={t("CONFIRM_MODAL.CONFIRM")}
        saveButtonProps={{
          visual: "destructive",
          isLoading: isDeleting,
          onClick: () => void handleDelete(),
        }}
        textToConfirm={confirmPhrase}
        label={t("CONFIRM_MODAL.LABEL", { phrase: confirmPhrase })}
        isMaxZIndex
      >
        <Text color="text.error.dark" visual="bodySemiBold">
          {t("CONFIRM_MODAL.WARNING")}
        </Text>
        <Text color="text.mid">{t("CONFIRM_MODAL.MESSAGE")}</Text>
      </ConfirmInputModal>
    </>
  );
};
